// The projection (RFC-0025 §4–5): an instruction/1 document → the render model the element draws, SSR serializes
// and the editor's preview shows. Pure and deterministic: the same text and options always yield the same model,
// which is what makes SSR output equal client output. The reader, its checks and resolution come from
// @instruction-md/spec — one reader for the editor, the registry, the CLI and this renderer.
//
// Rules, advisories, examples and output contracts become callouts (so the contents, "Rules at a glance" and the
// outline count them); every other kind is a `block` (blocks.ts draws it) — its kind, its typed attributes, its
// rendered children or its structured body. Every body stays visible: layout only arranges it (§8). The delivered
// view is the document resolved (§14): the agent's text, `instruction-text/1`.
import {
  KINDS, RULES, approval, checkWithoutWorkflows, read, resolveWith, valueFrom, type Approval,
  type Attrs, type Diagnostic, type DirectiveNode, type MarkdownNode, type Node as TreeNode, type Parameter, type ReadResult, type Source, type StructuredValue,
} from "@instruction-md/spec";
import { escapeHtml as e, type Heading } from "./md";
import { nodesHtml } from "./html";
import { explicitIds, fenceTitle, footnotesHtml, proseHtml, safeHref, safeImage, shownValue, type ProseScope } from "./prose";

export type View = "rendered" | "delivered" | "source";

/** Registry metadata shown in the header when the document came from a read. */
export interface RenderMeta {
  canonical?: string;
  versionId?: string;
  revision?: number;
  ref?: string;
  tag?: string;
  publisher?: string;
  /** `unsigned` | `signed` | `verified` (consumer-checked) | `revoked-key` */
  signature?: "unsigned" | "signed" | "verified" | "revoked-key";
  kid?: string;
  revoked?: { at: string; reason: string } | null;
  digest?: string;
}

export interface RenderOptions {
  /** Parameter values for the delivered view (§12). A value given as text (an element's attribute, a link's query) is
   *  read as its parameter's declared type: `"3"` an integer, `"a,b"` a list. */
  params?: Record<string, unknown>;
  /** The rendered view draws each parameter's value where it is used — the one given, else its declaration's
   *  default or example — as a host's inputs show them (the element's `inputs`). Without it, `${name}`. */
  showValues?: boolean;
  /** The document's URI, which its bases and includes resolve against (the delivered view). */
  uri?: string;
  /** Sources already loaded for the delivered view, by resolved URI: without them its bases and includes stay
   *  unresolved there (the view says so). */
  sources?: ReadonlyMap<string, Source | null>;
  allowImages?: boolean;
  /** Shift the document's own headings down, so an embedded document does not claim the host page's `<h1>`. */
  headingOffset?: number;
  meta?: RenderMeta;
  /** The host's approved integrations (§17): their targets (normalizeTarget), and whether connected objects are
   *  restricted to them. An object whose targets are all in the catalog is marked as an integration; under a
   *  restriction, one with a target outside it is marked as not approved — drawn, never removed. */
  integrations?: { targets: readonly string[]; restricted?: boolean };
}

export interface FormField { name: string; description?: string; required?: boolean; values?: string[]; default?: string; type?: string }

/** A block other than a callout: its kind and attributes, and what it holds. */
export interface Block {
  type: "block";
  kind: string;
  family: string;
  id?: string;
  title?: string;
  attrs: Attrs;
  children: Node[];
  /** A structured body: YAML, JSON, CSV, text, HTML or code. */
  value?: StructuredValue;
  /** An extension's binding from the front matter, when the kind is `alias:kind`. */
  extension?: { alias: string; id?: string; version?: string; required?: boolean };
  /** The source of a kind this renderer does not know. */
  source?: string;
  /** The fields an input form collects: the parameters its body references. */
  fields?: FormField[];
  /** A media block's preview — the image, the player — when the renderer may load images. */
  media?: string;
  /** A connected object against the host's integrations (RenderOptions.integrations). */
  integration?: Approval;
  /** A tab that is a fence written straight in its tabs: the tab names it, so its fence's own head is not drawn. */
  codeTab?: boolean;
}

export type Node =
  | { type: "markdown"; html: string }
  | {
      type: "callout"; kind: string; label: string; name?: string; title?: string; id?: string; html: string;
      /** A rule's condition, shown in its label (§6.1). */
      condition?: string;
      /** A rule's reason, rendered as Markdown. */
      becauseHtml?: string;
      /** An example of what to avoid. */
      avoid?: boolean;
      /** An output contract's response format and schema reference. */
      format?: string;
      schema?: string;
      /** The callout's own prose, without the blocks it holds — what the outline lists it by. */
      prose?: string;
    }
  | Block;

export interface RenderModel {
  spec: string;
  title?: string;
  description?: string;
  frontMatter: Record<string, unknown>;
  /** The document's record (its backmatter, §3.2), shown in the back matter; empty when there is none. */
  backmatter: Record<string, unknown>;
  headings: Heading[];
  nodes: Node[];
  /** What the agent receives: the resolved text, and whether everything it composes was there to resolve. */
  delivered: { text: string; status: "complete" | "partial" };
  source: string;
  /** The reader's diagnostics and the inline checks (§18); its errors are what a release refuses. A workflow body's
   *  checks against the workflow registry are the editor's and the validator's (`check`), not the renderer's: a
   *  renderer is not a workflow host (§9.1), and the registry would put the bundle over its cap. */
  diagnostics: Diagnostic[];
  /** The families of the kinds it uses (registry.json). */
  families: string[];
  /** The `headingOffset` the model was projected with, so chrome can match it. */
  headingOffset: number;
  /** The document's parameters as declared (§12): what a host's inputs ask for. */
  parameters: Parameter[];
  meta?: RenderMeta;
}

const LABEL: Record<string, string> = { must: "MUST", always: "ALWAYS", never: "NEVER", should: "SHOULD", avoid: "AVOID", may: "MAY", guardrail: "GUARDRAIL", note: "NOTE", info: "INFO", tip: "TIP", important: "IMPORTANT", warning: "WARNING", caution: "CAUTION" };
const ADVISORIES = new Set(["note", "info", "tip", "important", "warning", "caution"]);

const str = (v: unknown) => (typeof v === "string" ? v : undefined);

export function project(text: string, opts: RenderOptions = {}): RenderModel {
  const r = read(text);
  // a value given as text (an attribute, a link's query, an input) is read as its parameter's declared type
  const params = Object.fromEntries(Object.entries(opts.params ?? {}).map(([k, v]) => {
    const p = r.parameters.get(k);
    return [k, p && typeof v === "string" ? valueFrom(p, v) : v];
  }));
  const front = r.document.metadata.front;
  const back = r.document.metadata.back;
  const scope: ProseScope = {
    read: r,
    allowImages: !!opts.allowImages,
    headingOffset: Math.max(0, Math.min(5, Math.trunc(opts.headingOffset ?? 0))),
    used: explicitIds(r),
    headings: [],
    noteOrder: new Map(),
    notes: new Map(),
    definitions: linkDefinitions(r),
    sources: new Map((Array.isArray(back.sources) ? back.sources : []).filter((s): s is Record<string, unknown> => !!s && typeof s === "object" && typeof (s as Record<string, unknown>).id === "string").map((s) => [String(s.id), s])),
    values: opts.showValues ? Object.fromEntries([...r.parameters.values()].map((p) => [p.name, params[p.name] ?? p.default ?? p.example]).filter(([, v]) => v !== undefined)) : undefined,
  };
  const catalog = opts.integrations ? { targets: new Set(opts.integrations.targets), restricted: opts.integrations.restricted === true } : undefined;
  const nodes = r.document.body.flatMap((n) => projectNode(n, scope, r, catalog));
  const notes = footnotesHtml(scope);
  if (notes) nodes.push({ type: "markdown", html: notes });
  const offset = scope.headingOffset;
  const resolution = resolveWith(text, opts.sources ?? new Map(), { ...(opts.uri ? { uri: opts.uri } : {}), params });
  const families = new Set<string>();
  const walk = (ns: TreeNode[]) => { for (const n of ns) if (n.type === "directive") { const f = KINDS[n.kind]?.family; if (f) families.add(f); walk(n.children ?? []); } };
  walk(r.document.body);
  return {
    spec: String(front.spec ?? "1"),
    title: str(front.title) ?? scope.headings.find((h) => h.level === 1 + offset)?.text,
    description: str(front.description),
    frontMatter: front,
    backmatter: back,
    headings: scope.headings,
    nodes,
    delivered: { text: resolution.text, status: resolution.resolved.status },
    source: text,
    diagnostics: checkWithoutWorkflows(r),
    families: [...families].sort(),
    headingOffset: offset,
    parameters: [...r.parameters.values()],
    meta: opts.meta,
  };
}

/** Reference-style link definitions anywhere in the prose, so `[text][ref]` resolves in every segment. */
function linkDefinitions(r: ReadResult): string {
  const defs: string[] = [];
  for (const n of r.prose) {
    let fence: string | null = null;
    for (const line of n.source.split("\n")) {
      const f = /^[ \t]*(`{3,}|~{3,})/.exec(line);
      if (fence) { if (f && f[1][0] === fence[0] && f[1].length >= fence.length) fence = null; continue; }
      if (f) { fence = f[1]; continue; }
      if (/^ {0,3}\[(?!\^)[^\]]+\]:[ \t]*\S/.test(line)) defs.push(line.trim());
    }
  }
  return defs.length ? `\n\n${defs.join("\n")}\n` : "";
}

function prose(n: MarkdownNode, scope: ProseScope, r: ReadResult): Node {
  return { type: "markdown", html: proseHtml(n.source, scope, { literal: !r.prose.has(n) }) };
}

/** A callout's body: its prose, bare, and any block it holds drawn in place. */
function calloutHtml(nodes: Node[]): string {
  return nodes.map((n) => (n.type === "markdown" ? n.html : nodesHtml([n]))).join("");
}

/** A callout's own prose, the blocks it holds left out. */
const proseOf = (nodes: Node[]) => nodes.map((n) => (n.type === "markdown" ? n.html : "")).join("");

type Catalog = { targets: ReadonlySet<string>; restricted: boolean };

function children(nodes: TreeNode[] | undefined, scope: ProseScope, r: ReadResult, catalog?: Catalog): Node[] {
  return (nodes ?? []).flatMap((c) => projectNode(c, scope, r, catalog));
}

/** A `tabs` group's children, a fence written straight in it, outside any `tab`, a tab of its own (§8) titled by its
 *  `title`, its `filename` or its language. A view of the same children: the source and the agent's text keep the
 *  fences as written. */
function tabsChildren(nodes: TreeNode[] | undefined, scope: ProseScope, r: ReadResult, catalog?: Catalog): Node[] {
  const out: Node[] = [];
  for (const c of nodes ?? []) {
    if (c.type !== "markdown" || !r.prose.has(c)) { out.push(...projectNode(c, scope, r, catalog)); continue; }
    for (const part of fenceParts(c.source)) {
      const html = proseHtml(part.text, scope);
      if (part.info !== undefined) {
        const title = fenceTitle(part.info);
        out.push({ type: "block", kind: "tab", family: "layout", title, attrs: { title }, children: [{ type: "markdown", html }], codeTab: true });
      } else if (part.text.trim()) out.push({ type: "markdown", html });
    }
  }
  return out;
}

/** Markdown split into its top-level fences (each with its info string) and the text between them. */
function fenceParts(source: string): { text: string; info?: string }[] {
  const parts: { text: string; info?: string }[] = [];
  let text: string[] = [];
  let fence: { char: string; len: number; info: string; lines: string[] } | null = null;
  const flush = () => { if (text.length) parts.push({ text: text.join("\n") + "\n" }); text = []; };
  for (const line of source.replace(/\n$/, "").split("\n")) {
    if (fence) {
      fence.lines.push(line);
      const close = /^ {0,3}(`+|~+)[ \t]*$/.exec(line);
      if (close && close[1][0] === fence.char && close[1].length >= fence.len) { parts.push({ text: fence.lines.join("\n") + "\n", info: fence.info }); fence = null; }
      continue;
    }
    const open = /^ {0,3}(`{3,}|~{3,})(.*)$/.exec(line);
    if (open && !(open[1][0] === "`" && open[2].includes("`"))) { flush(); fence = { char: open[1][0], len: open[1].length, info: open[2].trim(), lines: [line] }; continue; }
    text.push(line);
  }
  if (fence) parts.push({ text: fence.lines.join("\n") + "\n", info: fence.info });
  flush();
  return parts;
}

function projectNode(n: TreeNode, scope: ProseScope, r: ReadResult, catalog?: Catalog): Node[] {
  if (n.type === "markdown") return [prose(n, scope, r)];
  const a = n.attrs;
  const id = str(a.id);
  const title = str(a.title);
  if (RULES.has(n.kind) || ADVISORIES.has(n.kind)) {
    // the word the author wrote (Always, Avoid, Must not); a negated should is drawn as avoid
    const negated = n.kind === "should" && a.not === true;
    const label = r.keywords.get(n) ?? (negated ? "SHOULD NOT" : LABEL[n.kind]);
    const kids = children(n.children, scope, r, catalog);
    return [{
      type: "callout", kind: negated ? "avoid" : n.kind, label, name: id, id, title,
      html: calloutHtml(kids), prose: proseOf(kids),
      // a condition interpolates as the rule's text does (§12)
      condition: scope.values && typeof a.if === "string" ? a.if.replace(/\$\{([A-Za-z_][A-Za-z0-9_.-]*)\}/g, (m, k: string) => (scope.values![k] === undefined ? m : shownValue(scope.values![k]))) : str(a.if),
      becauseHtml: typeof a.because === "string" ? proseHtml(a.because, scope, { inline: true }) : undefined,
    }];
  }
  if (n.kind === "example") {
    return [{ type: "callout", kind: "example", label: "Example", name: id, id, title, avoid: a.avoid === true, html: calloutHtml(children(n.children, scope, r, catalog)) }];
  }
  if (n.kind === "output") {
    return [{ type: "callout", kind: "output", label: "Output", name: id, id, title, format: str(a["response-format"]), schema: str(a.schema), html: calloutHtml(children(n.children, scope, r, catalog)) }];
  }
  const block: Block = { type: "block", kind: n.kind, family: KINDS[n.kind]?.family ?? (n.kind.includes(":") ? "extension" : "unknown"), id, title, attrs: a, children: [] };
  if (n.value) block.value = n.value;
  if (n.kind.includes(":")) {
    const alias = n.kind.split(":")[0];
    const binding = ((r.document.metadata.front.extensions ?? {}) as Record<string, Record<string, unknown> | undefined>)[alias];
    block.extension = { alias, id: str(binding?.id), version: str(binding?.version), required: binding?.required === true };
    // the fallback: its body as Markdown, never read for Instruction blocks (§5.3, §15)
    block.children = (n.children ?? []).map((c) => (c.type === "markdown" ? ({ type: "markdown", html: proseHtml(c.source, scope) } as Node) : ({ type: "markdown", html: "" } as Node)));
  } else if (!KINDS[n.kind]) {
    block.source = n.source;
  } else {
    block.children = n.kind === "tabs" ? tabsChildren(n.children, scope, r, catalog) : children(n.children, scope, r, catalog);
  }
  if (catalog) block.integration = approval(n.kind, a, catalog);
  if (n.kind === "form") block.fields = formFields(n, r);
  if (scope.allowImages && (block.family === "resource" || n.kind === "embed")) block.media = mediaPreview(n.kind, a);
  return [block];
}

const IMAGE = /\.(?:png|jpe?g|gif|webp|avif|svg)(?:[?#].*)?$/i;

/** The media itself: an image, or a player that loads nothing until it is played (preload none). */
function mediaPreview(kind: string, a: Attrs): string | undefined {
  const src = str(a.src), preview = str(a.preview);
  const alt = a.decorative === true ? "" : str(a.alt) ?? "";
  const img = (u: string | undefined) => { const ok = u ? safeImage(u) : null; return ok ? `<img class="media-img" src="${e(ok)}" alt="${e(alt)}"${a.decorative === true ? ' role="presentation"' : ""}>` : undefined; };
  if (kind === "image" || (src && IMAGE.test(src) && kind !== "drawing")) return img(src) ?? img(preview);
  const play = src ? safeHref(src) : null;
  if (kind === "video" && play) return `<video class="media-player" controls preload="none" src="${e(play)}"${preview && safeImage(preview) ? ` poster="${e(safeImage(preview)!)}"` : ""}${alt ? ` aria-label="${e(alt)}"` : ""}></video>`;
  if (kind === "audio" && play) return `<audio class="media-player" controls preload="none" src="${e(play)}"${alt ? ` aria-label="${e(alt)}"` : ""}></audio>`;
  return img(preview);
}

/** A form's fields: each parameter its body references, in order of first reference, with its declaration. */
function formFields(n: DirectiveNode, r: ReadResult): FormField[] {
  const seen: string[] = [];
  const walk = (nodes: TreeNode[]) => {
    for (const c of nodes) {
      if (c.type === "markdown") for (const m of c.source.matchAll(/\$\{([A-Za-z_][A-Za-z0-9_.-]*)\}/g)) { if (!seen.includes(m[1])) seen.push(m[1]); }
      else walk(c.children ?? []);
    }
  };
  walk(n.children ?? []);
  return seen.map((name) => {
    const p = r.parameters.get(name);
    return p ? { name, type: p.type, description: p.description, required: p.required, values: p.values, default: p.default !== undefined ? String(p.default) : undefined } : { name };
  });
}

/** Source view lines with 1-based numbers (escaped). */
export function sourceLines(text: string): { n: number; html: string }[] {
  return text.replace(/\n$/, "").split("\n").map((l, i) => ({ n: i + 1, html: e(l) }));
}
